import React, { useState, useRef, useEffect } from 'react';
import { styled } from 'styled-components';
import { Button, FlexBoxHorizontal, FlexBoxVertical, FlexItem, FlexBox, Label, TranslateTypography, TextField } from '../../atoms';
import { LuImagePlus } from 'react-icons/lu';
import CloseIcon from '@mui/icons-material/Close';
import palette from '../../../utils/colorPalette';
import i18n from '../../../utils/i18n'
import { translateText } from '../../../utils/translateText';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../../contexts/UserContext';

export const ImgUploadTab = ({ data, changeImg }) => {
  const { t } = useTranslation();
  const language = i18n.language;
  const [imgSrc, setImgSrc] = useState({});
  const [imageError, setImageError] = useState({}); // 추가
  const [selectedColorIndex, setSelectedColorIndex] = useState(null); // 선택된 컬러 인덱스 관리
  const [uploadObject, setUploadObject] = useState(null);
  const imgRef = useRef(null);
  const colorDivRefs = useRef({}); // 컬러 div들의 참조 추가
  const { user, permissions, hasPermission, loading: userLoading } = useUser();
  
  // data가 null이거나 color 속성이 없을 때를 처리
  const colors = data && data['color_list'] ? data['color_list'].split(',') : [];

  
  useEffect(() => {
    setUploadObject(null);
    if (data?.img) {
      const colorImages = Object.fromEntries(
        colors.map(color => [
          color.trim(), 
          updateTimestamp(`https://static-dashff.fnf.co.kr/interstellar/cad_img/X/${data.sesn}/${data.part_cd}_${color.trim()}.jpg`)
        ])
      );

      setImgSrc({
        [data.part_cd]: updateTimestamp(`${data.img}`),
        ...colorImages
      });
      
      // 각 이미지의 실제 로드 상태를 확인
      const checkImageLoad = (src, key) => {
        const img = new Image();
        img.onload = () => {
          setImageError(prev => ({ ...prev, [key]: false }));
        };
        img.onerror = () => {
          setImageError(prev => ({ ...prev, [key]: true }));
        };
        img.src = src;
      };
      
      // 대표 이미지 체크
      checkImageLoad(updateTimestamp(`${data.img}`), data.part_cd);
      
      // 컬러 이미지들 체크
      colors.forEach(color => {
        const colorKey = color.trim();
        const colorImageSrc = updateTimestamp(`https://static-dashff.fnf.co.kr/interstellar/cad_img/X/${data.sesn}/${data.part_cd}_${colorKey}.jpg`);
        checkImageLoad(colorImageSrc, colorKey);
      });
    } 
  }, [data]); // data 변경 감지

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // 파일이 선택되면 자동으로 업로드
      await handleUpload(file);
    }
  };

  // 이미지 압축 함수 수정
  const compressImage = async (file) => {
    // 300KB = 300 * 1024 bytes
    if (file.size <= 300 * 1024) {
      return file; // 300KB 이하면 원본 반환
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            },
            'image/jpeg',
            0.8  
          );
        };
      };
    });
  };

  const handleUpload = async (file) => {
    if (!data || !uploadObject) {
      alert('이미지를 업로드할 품번/컬러를 선택하세요.');
      return;
    }
    const formData = new FormData();

    // 원본 파일과 압축 파일 크기 비교를 위해 압축 파일 생성
    const compressedFile = await compressImage(file);

    // 원본 파일 추가
    formData.append('originalFile', file);
    // 압축 파일 추가
    formData.append('compressedFile', compressedFile);

    // 메타데이터 추가
    const isStyleUpload = uploadObject === data.part_cd;
    formData.append('part_cd', data.part_cd);
    if (!isStyleUpload) {
      formData.append('color_cd', uploadObject.replace(data.part_cd + '_', ''));
    }
    formData.append('sesn', data['sesn']);
    formData.append('brd_cd', data['brd_cd']);
  
    try {
      const response = await fetch('/api/s3-upload', {
        method: 'POST',
        body: formData,
      });
      const result = await response.text();
      alert(result);
  
      // 업로드 성공 시 이미지 초기화
      handleDeleteImage();

      // 스타일 이미지일때
      if (uploadObject === data.part_cd) {
        setImgSrc({
          ...imgSrc,
          [data.part_cd]: updateTimestamp(`${data.img}`),
        });
        changeImg(updateTimestamp(`${data.img}`));
        setImageError(prev => ({ ...prev, [data.part_cd]: false }));
      } else {
        setImgSrc({
          ...imgSrc,
          [uploadObject.split('_')[1]]: updateTimestamp(`https://static-dashff.fnf.co.kr/interstellar/cad_img/X/${data.sesn}/${uploadObject}.jpg`),
        });
        setImageError(prev => ({ ...prev, [uploadObject.split('_')[1]]: false }));
      }  

      setUploadObject(null);

    } catch (error) {
      console.error('업로드 오류:', error);
      alert('파일 업로드에 실패했습니다.');
    }
  };

  const fileInputRef = React.useRef();

  // 이미지 삭제 핸들러
  const handleDeleteImage = () => {
    // 파일 입력 필드 값 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // 동일한 파일을 다시 선택할 수 있도록 초기화
    }
  };

   // 드래그 앤 드롭 및 붙여넣기 핸들러
   const handleDrop = (event) => {
    event.preventDefault(); // 브라우저 기본 동작 방지
    
    if (!data || !uploadObject) {
      alert('이미지를 업로드할 품번/컬러를 선택하세요.');
      return;
    }
    
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      // 파일이 드롭되면 자동으로 업로드
      handleUpload(file);
    }
  };

  const handlePaste = async (event) => {
    if (!data) {
      alert('이미지를 업로드할 품번/컬러를 선택하세요.');
      handleDeleteImage();
      return;
    }
    const items = event.clipboardData.items;
    for (let item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          // 파일이 붙여넣기되면 자동으로 업로드
          await handleUpload(file);
        }
      }
    }
  };

  const handleDragOver = (event, color) => {
      event.preventDefault();
      if (color === data.part_cd) {
        setUploadObject(data.part_cd);
      } else {
        setUploadObject(data.part_cd + '_' + color.trim());
      }
  };

  const handleDragLeave = (event, color) => {
    event.preventDefault();
    setUploadObject(null); 
  };


  // 외부 클릭 핸들러 - uploadObject를 null로 초기화
  const handleOutsideClick = () => {
    setUploadObject(null);
  };

  return (
    <FlexBoxVertical 
      style={{justifyContent: 'flex-start'}}
      onClick={handleOutsideClick}
    >
      {/* 파일 입력 필드 (숨김) */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
      />
      {/* 상단부: 코드 및 브랜드 */}
      <FlexBoxHorizontal style={{ height: '40px', margin: '10px 10px 0 10px' }}>
        <Label style={{ width: '45%', margin: '3px' }}>
          <TranslateTypography variant="content2">
            {data && data['part_cd'] ? data['part_cd'] : '품번'}
          </TranslateTypography>
        </Label>
        <Label style={{ width: '45%', margin: '3px' }}>
          {language === 'ko' ? (
            <TranslateTypography variant="content2">
              {data && data['prdt_nm'] ? data['prdt_nm'] : '제품명 '}
            </TranslateTypography>
          ) : (
            <TranslateTypography variant="content2">
              {data && data['prdt_nm_eng'] ? data['prdt_nm_eng'] : 'Product Name'}
            </TranslateTypography>
          )}
        </Label>
      </FlexBoxHorizontal>
      

      {/* 상세정보 */}
      <FlexBoxVertical style={{height : '50%', borderBottom: `1.5px solid ${palette.border.default}`}}>
        <FlexBoxHorizontal style={{height: 'auto', justifyContent: 'space-between', width: '90%'}}>
        <TextField
            defaultValue={data && data.hasOwnProperty('size_range') && data['size_range'] !== null  ? data['size_range'] : ' '}
            placeholder="사이즈 범위"
            readOnly
            width={'160px'}
          />
        <TextField
            defaultValue={data && data.hasOwnProperty('fit_info') && data['fit_info'] !== null  ? data['fit_info'] : ' '}
            placeholder="핏"
            readOnly
            width={'160px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('tag_price') && data['tag_price'] !== null  ? data['tag_price'].toLocaleString() : ' '}
            placeholder="확정택가"
            readOnly
            width={'160px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('tag_price_cn') && data['tag_price_cn'] !== null  ? data['tag_price_cn'].toLocaleString() : ' '}
            placeholder="확정중국택가"
            readOnly
            width={'160px'}
          />
        </FlexBoxHorizontal>
        <FlexBoxHorizontal style={{height: 'auto', justifyContent: 'space-between', width: '90%'}}>
        <TextField
            defaultValue={data && data.hasOwnProperty('dsgnr_nm') && data['dsgnr_nm'] !== null   ? data['dsgnr_nm'] : ' '}
            placeholder="디자이너"
            readOnly
            width={'160px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('dsgnr_md_nm') && data['dsgnr_md_nm'] !== null   ? data['dsgnr_md_nm'] : ' '}
            placeholder="MD"
            readOnly
            width={'160px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('dsgnr_goods_nm') && data['dsgnr_goods_nm'] !== null   ? data['dsgnr_goods_nm'] : ' '}
            placeholder="소싱담당"
            readOnly
            width={'160px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('dsgnr_mtrls_nm') && data['dsgnr_mtrls_nm'] !== null   ? data['dsgnr_mtrls_nm'] : ' '}
            placeholder="소재담당"
            readOnly
            width={'160px'}
          />
        </FlexBoxHorizontal>

        {/* 이미지 박스 */}
        <FlexBox
          onClick={(e) => {
            if (!hasPermission('cadDashboard-image', 'edit')) { alert(language === 'en' ? 'You do not have permission.' : '권한이 없습니다.'); return; }
            e.stopPropagation(); // 이벤트 전파 중단
            data && setUploadObject(data.part_cd);
          }}
          onDrop={handleDrop}
          onDragOver={(event) => data && handleDragOver(event, data.part_cd)} // 수정된 핸들러 사용
          onPaste={handlePaste}
          onDragLeave={(event) => data && handleDragLeave(event, data.part_cd)}
          tabIndex={0}
          style={{
            width: '350px',
            height: '150px',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'pointer',
            borderRadius: '8px',
          }}
        >
          {data && uploadObject === data.part_cd ? (
            <ImagePlaceholder>
              <SimpleButton 
                onClick={(e) => {
                  // e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                style={{width: 'auto', marginBottom: '10px'}}
              >
                {translateText({
                  text: '이미지 선택',
                  globalLanguage: language,
                })}
              </SimpleButton>
              <div style={{ alignItems: 'center', gap: '5px', textAlign: 'center', fontSize: '12px', color: '#555' }}>
                {translateText({text: '이미지를 추가하세요.', globalLanguage: language})}<br /> {translateText({text: '(Ctrl+V, 드래그 & 드롭, 버튼 클릭)', globalLanguage: language})}
              </div>
          </ImagePlaceholder> 
          ) : data && !imageError[data.part_cd] ? (
            <img
            ref={imgRef}
            src={imgSrc[data.part_cd]}
            alt="Content"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />      
          ) : data ? (
            <ImagePlaceholder>
              <LuImagePlus style={{ width: '60%', height: '60%', color: 'rgb(218, 222, 224)' }} />
              <TranslateTypography variant="content1" style={{ color: 'rgb(187, 188, 189)', margin: '5px', fontSize: '12px' }}>
                이미지 업로드
              </TranslateTypography>
            </ImagePlaceholder>
          ) : (
            <div style={{width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '13px', color: 'rgb(119, 119, 119)'}}>{translateText({
              text: '스타일을 선택하세요',
              globalLanguage: language,
            })}</div>
          )}
        </FlexBox>

        <FlexBoxHorizontal style={{height: 'auto', justifyContent: 'space-between', width: '90%'}}>
        <TextField
            defaultValue={data && data.hasOwnProperty('mfac_compy_nm') && data['mfac_compy_nm'] !== null   ? data['mfac_compy_nm'] : ' '}
            placeholder="협력사"
            readOnly
            width={'200px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('fab_info') && data['fab_info'] !== null   ? data['fab_info'] : ' '}
            placeholder="소재 타입"
            readOnly
            width={'200px'}
          />
        </FlexBoxHorizontal>
        <FlexBox style={{height: 'auto', justifyContent: 'space-between', width: '90%'}}>
        <TextField
            defaultValue={data && data.hasOwnProperty('color_list') && data['color_list'] !== null ? data['color_list'] : ' '}
            placeholder="컬러"
            readOnly
            width={'100%'}
          />
        </FlexBox>
      </FlexBoxVertical>

      {/* 컬러 그리드 */}
      <ColorGridContainer>
        <ColorGrid>
          {colors.length > 0 ? (
          colors.map((color, index) => (
            <ColorItem key={index} >
              <ColorLabel>{color.trim()}</ColorLabel>
              <div
              ref={(el) => colorDivRefs.current[color.trim()] = el} 
              onClick={(e) => {
                if (!hasPermission('cadDashboard-image', 'edit')) { alert(language === 'en' ? 'You do not have permission.' : '권한이 없습니다.'); return; }
                e.stopPropagation();
                setUploadObject(data.part_cd + '_' + color.trim());
                colorDivRefs.current[color.trim()]?.focus();
              }}
              onDrop={handleDrop}
              onDragOver={(event) => handleDragOver(event, color)} // 수정된 핸들러 사용
              onPaste={handlePaste}
              onDragLeave={(event) => handleDragLeave(event, color)}
              tabIndex={0}
              >
              {data && uploadObject === data.part_cd + '_' + color.trim() ? (
                <ColorBox 
                title={color.trim()}
              >
                  <SimpleButton 
                    onClick={(e) => {
                      fileInputRef.current?.click();
                    }}
                    small
                  >
                    {translateText({
                      text: '이미지 선택',
                      globalLanguage: language,
                    })}
                  </SimpleButton>
              </ColorBox>
              ) : data && !imageError[color.trim()] ? (
                <img 
                src={imgSrc[color.trim()]} 
                alt="Content" 
                style={{
                  width: '100px',
                  height: '100px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              /> 
              ) : (
                <ColorBox
                title={color.trim()}
                >
                  <LuImagePlus style={{ width: '40%', height: '40%', color: 'rgb(218, 222, 224)' }} />
                  <TranslateTypography variant="content1" style={{ color: 'rgb(187, 188, 189)', margin: '5px', fontSize: '12px' }}>
                    이미지 업로드
                  </TranslateTypography>
                </ColorBox>
              )}
              </div>
            </ColorItem>
          ))
          ) : (
            <ColorItem></ColorItem>    
          )}
        </ColorGrid>
      </ColorGridContainer>

    </FlexBoxVertical>
  );
};

const updateTimestamp = (url) => {
  const urlObj = new URL(url);
  urlObj.searchParams.set('timestamp', new Date().getTime());
  return urlObj.toString();
};

// 컬러 그리드 관련 스타일 컴포넌트들
const ColorGridContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  margin: 10px 20px;
  width: 100%;
  height: 250px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
  width: 100%;
  height: 100%;
  max-width: 100%;
  justify-items: stretch;
  align-items: stretch;
`;

const ColorItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
`;

const ColorLabel = styled.div`
  font-size: 12px;
  color: #353339;
  text-align: center;
  word-break: break-all;
  font-weight: bold;
  padding: 2px 4px;
  border-radius: 4px;
  margin: 5px;
`;

const ColorBox = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

// 새로운 styled 컴포넌트 추가 (UploadBox와 ImageContainer 아래에)
const ImagePlaceholder = styled(FlexBox)`
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: #fafafa;
`;

// 깔끔한 통일 버튼 스타일
const SimpleButton = styled.button`
  padding: ${props => props.small ? '4px 8px' : '8px 16px'};
  background-color: #4f46e5;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: ${props => props.small ? '10px' : '12px'};
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  width: ${props => props.small ? '70px' : '100px'};
  
  &:hover {
    background-color: #4338ca;
  }
  
  &:active {
    background-color: #3730a3;
  }
  
  ${props => props.small && `
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  `}
`;

